/**
 * Trust Console trace model.
 *
 * `TrustTrace` is the data contract behind the slide-up Trust Console
 * (components/suite/TrustConsole.tsx). It mirrors the five sections of the
 * "Reasoning trace" panel in docs/mockups/suite-B-v4-console.html:
 *
 *   Routing       — how the query was routed (fund/corpus, tool, access tier,
 *                   classification, entitlement gate, policy).
 *   Retrieval     — the ranked candidate set, what survived rerank/gating, and
 *                   the final context window that was actually generated from.
 *   Verification  — the post-generation grounding checks + grounding score.
 *   Citations     — the resolved citation chips (doc → section → chunk → sim),
 *                   each linkable back into the live source rail.
 *   Telemetry     — per-stage latency, model, token counts, and a trace id.
 *
 * This is a pure data model — no React, no DOM. The console renders whatever
 * `TrustTrace` it is handed. For U1 the SuitePage wires `STUB_TRACE` (the
 * management-fee answer from the mockup); a later phase swaps in live
 * per-answer trace data emitted by the worker.
 */

/* ─── Routing ─────────────────────────────────────────────────────────── */

export type GateStatus = "pass" | "fail" | "na";

export interface TrustRouting {
  /** Resolved fund / corpus, e.g. "Outpost A-2 → data-room://outpost-a2". */
  fund: string;
  /** Machine corpus handle, e.g. "data-room://outpost-a2". */
  corpus: string;
  /** Optional corpus annotation, e.g. "Q2 2026, 847 docs". */
  corpusNote?: string;
  /** Tool invoked, e.g. "investor_qa". */
  tool: string;
  /** Optional tool annotation, e.g. "v3 · grounded-answer mode". */
  toolNote?: string;
  /** Access tier the query ran under, e.g. "Extended". */
  accessTier: string;
  /** Optional access-tier annotation. */
  accessTierNote?: string;
  /** Query classification, e.g. "terms/fees". */
  classification: string;
  /** Classification confidence in [0, 1]. */
  classificationConfidence?: number;
  /** Entitlement gate outcome. */
  entitlementGate: {
    status: GateStatus;
    detail?: string;
  };
  /** Optional generation policy summary, e.g. "closed-world · cite-or-abstain". */
  policy?: string;
  /** Optional resolution latency annotation, e.g. "resolved in 0.04s". */
  resolvedIn?: string;
}

/* ─── Retrieval ───────────────────────────────────────────────────────── */

export type RetrievalStatus =
  | "selected"
  | "pinned"
  | "reranked-out"
  | "below-cutoff"
  | "gated-out";

/** Which scorer produced a candidate's displayed score (#146 defect 2). */
export type CandidateScorer = "rerank" | "rrf" | "none";

export interface RetrievalCandidate {
  /**
   * DISPLAY score in [0, 1].
   *
   * - scorer "rerank": the reranker's own relevance score, as returned.
   * - scorer "rrf":    the hybrid fusion score expressed as a SHARE of the best
   *                    possible fusion score (both arms ranking the chunk #1).
   *                    Raw RRF only ever spans ~0.005–0.016, so printed at two
   *                    decimals it collapsed to 0.00/0.01/0.02 for every chunk
   *                    ever retrieved — a column that could not tell a healthy
   *                    retrieval from a collapsed one (#146 defect 1). The
   *                    rescale is monotone: ordering is unchanged.
   */
  score: number;
  /** 1-based position in the served set — the order the answer actually saw. */
  rank?: number;
  /** Which scorer produced `score`. */
  scorer?: CandidateScorer;
  /** The untransformed underlying number (raw RRF value / reranker relevance). */
  rawScore?: number;
  /** Source document name, e.g. "Outpost A-2 PPM". */
  doc: string;
  /** Section / location within the doc, e.g. "§4.2 Management Fee · p.38". */
  section: string;
  /** What happened to this candidate in the pipeline. */
  status: RetrievalStatus;
  /** If selected/pinned, the citation index it maps to ([1], [2], …). */
  citationIndex?: number;
}

export interface RetrievalContext {
  /** Number of chunks that reached the model. */
  chunks: number;
  /** Token count of the final context window. */
  tokens: number;
  /** Reranker id, e.g. "gp-rerank-lite". */
  reranker: string;
  /** Human label for the scorer behind the score column (#146 AC2). */
  scorer?: string;
  /** Score cutoff used for selection. */
  cutoff: number;
}

export interface TrustRetrieval {
  /** Ranked candidate set, highest score first. */
  candidates: RetrievalCandidate[];
  /** The final context window the answer was generated from. */
  finalContext: RetrievalContext;
}

/* ─── Verification ────────────────────────────────────────────────────── */

export interface VerificationCheck {
  /** Short label, e.g. "Every figure traced to a source". */
  label: string;
  /** Supporting detail line. */
  detail?: string;
  /** Whether this check passed. */
  pass: boolean;
}

export interface TrustVerification {
  checks: VerificationCheck[];
  /** Grounding score in [0, 1]. */
  groundingScore: number;
  /** Abstain threshold below which the system declines to answer. */
  abstainThreshold: number;
}

/* ─── Citations ───────────────────────────────────────────────────────── */

export interface TrustCitation {
  /** Citation index ([1], [2], …) — matches the inline chip + source card. */
  index: number;
  /** Source document name. */
  doc: string;
  /** Section / location within the doc. */
  section: string;
  /** Page label, e.g. "p.38". */
  page?: string;
  /** Retrieval chunk id, e.g. "a2-ppm-0042". */
  chunkId?: string;
  /** Similarity score in [0, 1]. */
  sim?: number;
}

/* ─── Telemetry ───────────────────────────────────────────────────────── */

export interface TrustLatency {
  /** Milliseconds spent in retrieval. */
  retrieve: number;
  /** Milliseconds spent in rerank. */
  rerank: number;
  /** Milliseconds spent in generation. */
  generate: number;
  /** Milliseconds spent in verification. */
  verify: number;
}

export interface TrustTelemetry {
  latency: TrustLatency;
  /** Model id, e.g. "gp-answer-3". */
  model: string;
  /** Optional model annotation, e.g. "claude-backed · temp 0.0 · seeded". */
  modelNote?: string;
  /** Tokens fed to the model. */
  tokensIn: number;
  /** Tokens produced by the model. */
  tokensOut: number;
  /** Opaque trace id for correlation, e.g. "trc_9f3a·outpost-a2·2026-06-25". */
  traceId: string;
}

/* ─── Top-level trace ─────────────────────────────────────────────────── */

export interface TrustTrace {
  /** The user query this trace explains. */
  query: string;
  /** Collapsed-strip summary: "Grounded · N sources · verified". */
  summary: {
    /** Outcome word, e.g. "Grounded". */
    status: string;
    /** Source count for the strip. */
    sourceCount: number;
    /** Whether the answer passed verification. */
    verified: boolean;
  };
  routing: TrustRouting;
  retrieval: TrustRetrieval;
  verification: TrustVerification;
  citations: TrustCitation[];
  telemetry: TrustTelemetry;
}

/** Total pipeline latency in ms (sum of the per-stage latencies). */
export function totalLatencyMs(t: TrustTrace): number {
  const { retrieve, rerank, generate, verify } = t.telemetry.latency;
  return retrieve + rerank + generate + verify;
}

/** Human latency for the collapsed strip, e.g. "1.8s". */
export function formatLatency(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/** Grounding sentinel: WS4 sends null when the grounding monitor flag is off.
 *  The console renders "not run" for this value — NEVER a fake 0.00 (which
 *  would read as a failed answer). Kept out of band as -1 so the numeric
 *  `groundingScore` field can stay a plain `number`. */
export const GROUNDING_NOT_RUN = -1;

/* ════════════════════════════════════════════════════════════════════════
   EMPTY_TRACE — the calm pre-first-answer steady state.

   With the worker `STREAM_STEPS` flag OFF (prod today) no `trace` event ever
   arrives, so the console has nothing live to show. It MUST show THIS calm
   "awaiting question" state — NOT the frozen management-fee STUB_TRACE — so a
   fresh suite never displays stale, fabricated data. The console detects this
   sentinel via `isEmptyTrace()` and renders its empty affordances.

   Every section is intentionally empty (0 candidates / checks / citations),
   the summary reads "Awaiting question · 0 sources", grounding is "not run",
   and telemetry carries no trace id. This reads as deliberate, not broken.
   ════════════════════════════════════════════════════════════════════════ */
export function awaitingTrace(): TrustTrace {
  return {
    query: "",
    summary: { status: "Awaiting question", sourceCount: 0, verified: false },
    routing: {
      fund: "—",
      corpus: "—",
      tool: "investor_qa",
      accessTier: "—",
      classification: "—",
      entitlementGate: { status: "na" },
    },
    retrieval: {
      candidates: [],
      finalContext: { chunks: 0, tokens: 0, reranker: "—", cutoff: 0 },
    },
    verification: {
      checks: [],
      groundingScore: GROUNDING_NOT_RUN,
      abstainThreshold: 0.7,
    },
    citations: [],
    telemetry: {
      latency: { retrieve: 0, rerank: 0, generate: 0, verify: 0 },
      model: "—",
      tokensIn: 0,
      tokensOut: 0,
      traceId: "",
    },
  };
}

/** A stable, shared instance of the empty pre-first-answer trace. */
export const EMPTY_TRACE: TrustTrace = awaitingTrace();

/** True when a trace is the calm pre-first-answer state (no live answer yet).
 *  Detected structurally (no query + no citations + no retrieval candidates)
 *  so it holds for both the shared EMPTY_TRACE and any fresh awaitingTrace(). */
export function isEmptyTrace(t: TrustTrace): boolean {
  return (
    !t.query &&
    t.citations.length === 0 &&
    t.retrieval.candidates.length === 0 &&
    t.summary.sourceCount === 0
  );
}

/* ════════════════════════════════════════════════════════════════════════
   normalizeTrace — WS-T1 null-crash fix.

   Deep-fills a raw server trace object into a valid TrustTrace. When the
   worker emits `trace` SSE events with null/undefined/non-finite fields,
   this fills them with sensible defaults so the console never crashes on
   `.toFixed()` or other field access.

   Mapping:
   - `groundingScore` null/undefined/non-number → GROUNDING_NOT_RUN (-1)
   - `abstainThreshold` non-finite → 0.7
   - Missing `checks`/`candidates`/`citations` arrays → []
   - Missing latency fields → 0
   - Missing strings → "—" (em-dash for routing) or "" (empty for others)
   - `sourceCount` non-finite → 0
   - Candidate/citation `score`/`sim` non-finite → left as non-finite (guard at render)
   - `finalContext` fields defaulted (chunks 0, tokens 0, reranker "—", cutoff 0)

   If raw is not an object, returns a fresh awaitingTrace() (calm state).
   ════════════════════════════════════════════════════════════════════════ */
export function normalizeTrace(raw: unknown): TrustTrace {
  // Not an object? Return the calm awaiting state.
  if (typeof raw !== "object" || raw === null) {
    return awaitingTrace();
  }

  const r = raw as any; // Use any for the raw input, then type strictly for each output

  // Normalize routing
  const routing: TrustRouting = {
    fund: typeof r.routing?.fund === "string" ? r.routing.fund : "—",
    corpus: typeof r.routing?.corpus === "string" ? r.routing.corpus : "—",
    corpusNote:
      typeof r.routing?.corpusNote === "string" ? r.routing.corpusNote : undefined,
    tool: typeof r.routing?.tool === "string" ? r.routing.tool : "investor_qa",
    toolNote:
      typeof r.routing?.toolNote === "string" ? r.routing.toolNote : undefined,
    accessTier: typeof r.routing?.accessTier === "string" ? r.routing.accessTier : "—",
    accessTierNote:
      typeof r.routing?.accessTierNote === "string" ? r.routing.accessTierNote : undefined,
    classification: typeof r.routing?.classification === "string" ? r.routing.classification : "—",
    classificationConfidence:
      typeof r.routing?.classificationConfidence === "number"
        ? r.routing.classificationConfidence
        : undefined,
    entitlementGate: {
      status:
        r.routing?.entitlementGate?.status === "pass" ||
        r.routing?.entitlementGate?.status === "fail" ||
        r.routing?.entitlementGate?.status === "na"
          ? r.routing.entitlementGate.status
          : "na",
      detail:
        typeof r.routing?.entitlementGate?.detail === "string"
          ? r.routing.entitlementGate.detail
          : undefined,
    },
    policy: typeof r.routing?.policy === "string" ? r.routing.policy : undefined,
    resolvedIn:
      typeof r.routing?.resolvedIn === "string" ? r.routing.resolvedIn : undefined,
  };

  // Normalize retrieval candidates
  const candidates = Array.isArray(r.retrieval?.candidates)
    ? (r.retrieval.candidates as unknown[])
        .map((c: any) => {
          if (typeof c !== "object" || c === null) return null;
          return {
            score: typeof c.score === "number" ? c.score : 0,
            // #146: rank / scorer / rawScore are additive trace fidelity fields.
            // A trace from an older worker simply omits them and the console
            // falls back to its pre-#146 rendering.
            rank:
              typeof c.rank === "number" && Number.isFinite(c.rank) && c.rank > 0
                ? Math.trunc(c.rank)
                : undefined,
            scorer:
              c.scorer === "rerank" || c.scorer === "rrf" || c.scorer === "none"
                ? c.scorer
                : undefined,
            rawScore:
              typeof c.rawScore === "number" && Number.isFinite(c.rawScore)
                ? c.rawScore
                : undefined,
            doc: typeof c.doc === "string" ? c.doc : "—",
            section: typeof c.section === "string" ? c.section : "—",
            status:
              c.status === "selected" ||
              c.status === "pinned" ||
              c.status === "reranked-out" ||
              c.status === "below-cutoff" ||
              c.status === "gated-out"
                ? c.status
                : "below-cutoff",
            citationIndex: typeof c.citationIndex === "number" ? c.citationIndex : undefined,
          };
        })
        .filter((c) => c !== null)
    : [];

  // Normalize final context
  const ctxObj = r.retrieval?.finalContext as any;
  const finalContext: RetrievalContext = {
    chunks: typeof ctxObj?.chunks === "number" && Number.isFinite(ctxObj.chunks) ? ctxObj.chunks : 0,
    tokens: typeof ctxObj?.tokens === "number" && Number.isFinite(ctxObj.tokens) ? ctxObj.tokens : 0,
    reranker: typeof ctxObj?.reranker === "string" ? ctxObj.reranker : "—",
    scorer: typeof ctxObj?.scorer === "string" && ctxObj.scorer ? ctxObj.scorer : undefined,
    cutoff: typeof ctxObj?.cutoff === "number" && Number.isFinite(ctxObj.cutoff) ? ctxObj.cutoff : 0,
  };

  // Normalize retrieval
  const retrieval: TrustRetrieval = {
    candidates: candidates as RetrievalCandidate[],
    finalContext,
  };

  // Normalize verification checks
  const checks = Array.isArray(r.verification?.checks)
    ? (r.verification.checks as unknown[])
        .map((c: any) => {
          if (typeof c !== "object" || c === null) return null;
          return {
            label: typeof c.label === "string" ? c.label : "—",
            detail: typeof c.detail === "string" ? c.detail : undefined,
            pass: c.pass === true,
          };
        })
        .filter((c) => c !== null)
    : [];

  // Normalize grounding score: null/undefined/non-finite → GROUNDING_NOT_RUN
  let groundingScore: number;
  const rawScore = r.verification?.groundingScore;
  if (typeof rawScore === "number" && Number.isFinite(rawScore)) {
    groundingScore = rawScore;
  } else {
    groundingScore = GROUNDING_NOT_RUN;
  }

  // Normalize abstain threshold
  let abstainThreshold = 0.7;
  const rawAbstain = r.verification?.abstainThreshold;
  if (typeof rawAbstain === "number" && Number.isFinite(rawAbstain)) {
    abstainThreshold = rawAbstain;
  }

  const verification: TrustVerification = {
    checks: checks as VerificationCheck[],
    groundingScore,
    abstainThreshold,
  };

  // Normalize citations
  const citations = Array.isArray(r.citations)
    ? (r.citations as unknown[])
        .map((c: any) => {
          if (typeof c !== "object" || c === null) return null;
          const sim = c.sim;
          return {
            index: typeof c.index === "number" ? c.index : 1,
            doc: typeof c.doc === "string" ? c.doc : "—",
            section: typeof c.section === "string" ? c.section : "—",
            page: typeof c.page === "string" ? c.page : undefined,
            chunkId: typeof c.chunkId === "string" ? c.chunkId : undefined,
            sim: typeof sim === "number" && Number.isFinite(sim) ? sim : undefined,
          };
        })
        .filter((c) => c !== null)
    : [];

  // Normalize telemetry latency
  const latencyObj = r.telemetry?.latency as any;
  const latency: TrustLatency = {
    retrieve:
      typeof latencyObj?.retrieve === "number" && Number.isFinite(latencyObj.retrieve)
        ? latencyObj.retrieve
        : 0,
    rerank:
      typeof latencyObj?.rerank === "number" && Number.isFinite(latencyObj.rerank)
        ? latencyObj.rerank
        : 0,
    generate:
      typeof latencyObj?.generate === "number" && Number.isFinite(latencyObj.generate)
        ? latencyObj.generate
        : 0,
    verify:
      typeof latencyObj?.verify === "number" && Number.isFinite(latencyObj.verify)
        ? latencyObj.verify
        : 0,
  };

  // Normalize telemetry
  const telemetry: TrustTelemetry = {
    latency,
    model: typeof r.telemetry?.model === "string" ? r.telemetry.model : "—",
    modelNote: typeof r.telemetry?.modelNote === "string" ? r.telemetry.modelNote : undefined,
    tokensIn:
      typeof r.telemetry?.tokensIn === "number" && Number.isFinite(r.telemetry.tokensIn)
        ? r.telemetry.tokensIn
        : 0,
    tokensOut:
      typeof r.telemetry?.tokensOut === "number" && Number.isFinite(r.telemetry.tokensOut)
        ? r.telemetry.tokensOut
        : 0,
    traceId: typeof r.telemetry?.traceId === "string" ? r.telemetry.traceId : "",
  };

  // Normalize summary
  const summaryObj = r.summary as any;
  const sourceCount =
    typeof summaryObj?.sourceCount === "number" && Number.isFinite(summaryObj.sourceCount)
      ? summaryObj.sourceCount
      : 0;

  const summary = {
    status: typeof summaryObj?.status === "string" ? summaryObj.status : "—",
    sourceCount,
    verified: summaryObj?.verified === true,
  };

  // Normalize query
  const query = typeof r.query === "string" ? r.query : "";

  return {
    query,
    summary,
    routing,
    retrieval,
    verification,
    citations: citations as TrustCitation[],
    telemetry,
  };
}

/* ════════════════════════════════════════════════════════════════════════
   STUB_TRACE — the management-fee answer from suite-B-v4-console.html.

   DEV/DEMO ONLY. This is the frozen mockup example. It is NOT the prod default:
   the SuitePage now shows EMPTY_TRACE before the first answer and the live
   per-answer trace (WS4 `onTrace`) after. Do NOT wire STUB_TRACE as the
   console's steady state — shipping it would display stale, fabricated data on
   a fresh suite. Kept for design reference / demos / storybook-style previews.
   ════════════════════════════════════════════════════════════════════════ */
export const STUB_TRACE: TrustTrace = {
  query:
    "What is the management fee for Outpost A-2, and does it step down after the investment period?",
  summary: { status: "Grounded", sourceCount: 3, verified: true },
  routing: {
    fund: "Outpost A-2",
    corpus: "data-room://outpost-a2",
    corpusNote: "Q2 2026, 847 docs",
    tool: "investor_qa",
    toolNote: "v3 · grounded-answer mode",
    accessTier: "Extended",
    accessTierNote: "gated corpus in scope",
    classification: "terms/fees",
    classificationConfidence: 0.96,
    entitlementGate: {
      status: "pass",
      detail: "viewer entitled to L1–L4; L5 side letters withheld",
    },
    policy: "closed-world · cite-or-abstain · no-fabrication",
    resolvedIn: "resolved in 0.04s",
  },
  retrieval: {
    candidates: [
      {
        score: 0.91,
        doc: "Outpost A-2 PPM",
        section: "§4.2 Management Fee · p.38",
        status: "selected",
        citationIndex: 1,
      },
      {
        score: 0.88,
        doc: "Outpost A-2 LPA Summary",
        section: "Art. VI · p.12",
        status: "selected",
        citationIndex: 2,
      },
      {
        score: 0.84,
        doc: "GRAF II PPM",
        section: "§3.1 Management Fee · p.29",
        status: "pinned",
        citationIndex: 3,
      },
      {
        score: 0.71,
        doc: "Outpost A-2 PPM",
        section: "§4.4 Fee Offsets · p.41",
        status: "reranked-out",
      },
      {
        score: 0.66,
        doc: "Portfolio Operating Review Q2'26",
        section: 'tab "Fees"',
        status: "below-cutoff",
      },
      {
        score: 0.52,
        doc: "Meridian Side Letter — GRAF II",
        section: "L5 · restricted",
        status: "gated-out",
      },
    ],
    finalContext: {
      chunks: 3,
      tokens: 1184,
      reranker: "gp-rerank-lite",
      cutoff: 0.8,
    },
  },
  verification: {
    checks: [
      {
        label: "Bindings present & copied verbatim",
        detail:
          "3/3 figure bindings matched source text exactly (1.5%, 1.25%, 1.75%)",
        pass: true,
      },
      {
        label: "Every figure traced to a source",
        detail: "1.5% → [1] §4.2 · 1.25% → [2] Art. VI · 1.5% extension → [1] §4.2",
        pass: true,
      },
      {
        label: "No uncited claims",
        detail: "0 sentences without a supporting citation",
        pass: true,
      },
      {
        label: "Fabrication guard",
        detail: "closed-world check passed — no figures outside retrieved context",
        pass: true,
      },
    ],
    groundingScore: 0.98,
    abstainThreshold: 0.7,
  },
  citations: [
    {
      index: 1,
      doc: "Outpost A-2 PPM",
      section: "§4.2 Management Fee",
      page: "p.38",
      chunkId: "a2-ppm-0042",
      sim: 0.91,
    },
    {
      index: 2,
      doc: "Outpost A-2 LPA Summary",
      section: "Art. VI",
      page: "p.12",
      chunkId: "a2-lpa-0012",
      sim: 0.88,
    },
    {
      index: 3,
      doc: "GRAF II PPM",
      section: "§3.1 Management Fee",
      page: "p.29",
      chunkId: "g2-ppm-0029",
      sim: 0.84,
    },
  ],
  telemetry: {
    latency: { retrieve: 412, rerank: 188, generate: 1106, verify: 136 },
    model: "gp-answer-3",
    modelNote: "claude-backed · temp 0.0 · seeded",
    tokensIn: 1184,
    tokensOut: 96,
    traceId: "trc_9f3a·outpost-a2·2026-06-25",
  },
};
